import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Folder,
  FileText,
  Image as ImageIcon,
  Film,
  HardDrive,
  Upload,
  Search,
  Eye,
  Grid,
  List,
  LayoutList,
  LayoutGrid,
  Download,
  Music,
  Archive,
  FileSpreadsheet,
  Layers,
  ChevronDown
} from 'lucide-react';
import type { WorkspaceFile, FileTypeFilter } from '@sahaj/types';
import { useWorkspace } from '../../context/WorkspaceContext';
import { useLanguage } from '../../hooks/useLanguage';
import { api } from '../../lib/api';
import { fileResourceService } from '../../lib/FileResourceService';
import { FileUploadModal } from './FileUploadModal';
import { FilePreviewModal } from './FilePreviewModal';
import { FileThumbnail } from './FileThumbnail';

type SortOption = 'DATE_DESC' | 'DATE_ASC' | 'NAME_ASC' | 'NAME_DESC' | 'SIZE_DESC' | 'SIZE_ASC';
type ViewMode = 'grid' | 'gallery' | 'list' | 'compact' | 'media';

export const FilesView: React.FC = () => {
  const { currentWorkspace } = useWorkspace();
  const { t } = useLanguage();

  const [files, setFiles] = useState<WorkspaceFile[]>([]);
  const [activeFilter, setActiveFilter] = useState<FileTypeFilter>('ALL');
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState<SortOption>('DATE_DESC');
  const [viewMode, setViewMode] = useState<ViewMode>(() => {
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem('sahaj.files.viewMode');
      if (['grid', 'gallery', 'list', 'compact', 'media'].includes(saved || '')) return saved as ViewMode;
    }
    return 'grid';
  });
  const [isLoading, setIsLoading] = useState(false);

  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [previewFile, setPreviewFile] = useState<WorkspaceFile | null>(null);

  const handleViewModeChange = (mode: ViewMode) => {
    setViewMode(mode);
    if (typeof window !== 'undefined') {
      localStorage.setItem('sahaj.files.viewMode', mode);
    }
  };

  const fetchFiles = useCallback(async () => {
    if (!currentWorkspace) return;
    try {
      setIsLoading(true);

      const res = await api.files.list(currentWorkspace.id, {
        filter: activeFilter,
        ...(searchQuery.trim() ? { query: searchQuery.trim() } : {}),
        size: 100
      });
      setFiles(res.data);
    } catch (err) {
      console.error('Failed to load files', err);
    } finally {
      setIsLoading(false);
    }
  }, [currentWorkspace, activeFilter, searchQuery]);

  useEffect(() => {
    fetchFiles();
  }, [fetchFiles]);

  const filteredAndSortedFiles = useMemo(() => {
    let result = [...files];

    result.sort((a, b) => {
      if (sortBy === 'DATE_DESC') {
        return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
      }
      if (sortBy === 'DATE_ASC') {
        return new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
      }
      if (sortBy === 'NAME_ASC') {
        return a.fileName.localeCompare(b.fileName);
      }
      if (sortBy === 'NAME_DESC') {
        return b.fileName.localeCompare(a.fileName);
      }
      if (sortBy === 'SIZE_DESC') {
        return b.fileSize - a.fileSize;
      }
      if (sortBy === 'SIZE_ASC') {
        return a.fileSize - b.fileSize;
      }
      return 0;
    });

    return result;
  }, [files, sortBy]);

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const formatDate = (isoString: string) => {
    return new Date(isoString).toLocaleDateString([], {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const getFileIcon = (fileType: string, category?: string) => {
    const type = fileType.toLowerCase();
    if (category === 'IMAGE' || type.startsWith('image/')) return <ImageIcon className="h-5 w-5 text-indigo-500 flex-shrink-0" />;
    if (category === 'VIDEO' || type.startsWith('video/')) return <Film className="h-5 w-5 text-rose-500 flex-shrink-0" />;
    if (category === 'AUDIO' || type.startsWith('audio/')) return <Music className="h-5 w-5 text-purple-500 flex-shrink-0" />;
    if (category === 'SPREADSHEET' || type.includes('excel') || type.includes('spreadsheet')) return <FileSpreadsheet className="h-5 w-5 text-emerald-500 flex-shrink-0" />;
    if (category === 'ARCHIVE' || type.includes('zip') || type.includes('tar')) return <Archive className="h-5 w-5 text-amber-500 flex-shrink-0" />;
    if (type === 'application/pdf') return <FileText className="h-5 w-5 text-red-500 flex-shrink-0" />;
    return <FileText className="h-5 w-5 text-blue-500 flex-shrink-0" />;
  };

  const filterTabs: { id: FileTypeFilter; label: string; icon: React.ReactNode }[] = [
    { id: 'ALL', label: t('files.allFiles'), icon: <HardDrive className="h-3.5 w-3.5" /> },
    { id: 'DOCUMENT', label: t('files.documents'), icon: <FileText className="h-3.5 w-3.5" /> },
    { id: 'IMAGE', label: t('files.images'), icon: <ImageIcon className="h-3.5 w-3.5" /> },
    { id: 'MEDIA', label: t('files.media'), icon: <Film className="h-3.5 w-3.5" /> },
    { id: 'SPREADSHEET', label: 'Spreadsheets', icon: <FileSpreadsheet className="h-3.5 w-3.5" /> },
    { id: 'ARCHIVE', label: 'Archives', icon: <Archive className="h-3.5 w-3.5" /> },
    { id: 'MY_UPLOADS', label: t('files.myUploads'), icon: <Folder className="h-3.5 w-3.5" /> },
  ];

  return (
    <div className="flex-1 flex flex-col h-full bg-background text-foreground overflow-hidden">
      {/* Header matching AppHeader layout and styling */}
      <header className="h-16 border-b border-border bg-card/40 px-5 flex items-center justify-between gap-4 select-none flex-shrink-0">
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-9 h-9 rounded-xl bg-primary/10 text-primary flex items-center justify-center flex-shrink-0 shadow-sm">
            <Folder className="h-5 w-5" />
          </div>
          <div className="min-w-0">
            <h2 className="font-bold text-foreground text-sm sm:text-base truncate flex items-center gap-2">
              <span>{t('files.title')}</span>
              <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-primary/10 text-primary border border-primary/20">
                {files.length}
              </span>
            </h2>
            <p className="text-xs text-muted-foreground truncate">
              {t('files.subtitle')}
            </p>
          </div>
        </div>

        <button
          onClick={() => setIsUploadModalOpen(true)}
          className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-lg bg-primary text-primary-foreground font-semibold text-xs hover:bg-primary/90 transition shadow-sm"
        >
          <Upload className="h-4 w-4" />
          <span className="hidden sm:inline">{t('files.uploadFile')}</span>
        </button>
      </header>

      {/* Toolbar: Filters, Search, View Mode, Sorting */}
      <div className="px-5 py-2.5 border-b border-border bg-card/20 flex flex-wrap items-center justify-between gap-3 select-none">
        {/* Category Pills */}
        <div className="flex items-center gap-1.5 overflow-x-auto no-scrollbar">
          {filterTabs.map((tab) => {
            const isActive = activeFilter === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveFilter(tab.id)}
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition select-none ${
                  isActive
                    ? 'bg-primary text-primary-foreground shadow-sm'
                    : 'bg-muted/70 hover:bg-muted text-muted-foreground hover:text-foreground'
                }`}
              >
                {tab.icon}
                <span>{tab.label}</span>
              </button>
            );
          })}
        </div>

        {/* Controls: Search, Sort, 5 View Switcher */}
        <div className="flex items-center gap-2 flex-wrap">
          {/* Search Input */}
          <div className="relative flex items-center bg-muted/60 rounded-lg px-3 py-1.5 w-36 sm:w-48 border border-border/50">
            <Search className="h-3.5 w-3.5 text-muted-foreground mr-2 flex-shrink-0" />
            <input
              type="text"
              placeholder={t('files.searchPlaceholder')}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="bg-transparent border-none focus:outline-none text-xs w-full placeholder:text-muted-foreground text-foreground"
            />
          </div>

          {/* Sort selector */}
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value as SortOption)}
            className="h-8 px-2 bg-card border border-border rounded-lg text-xs font-medium text-foreground focus:outline-none focus:ring-2 focus:ring-primary/20"
          >
            <option value="DATE_DESC">{t('files.newestFirst')}</option>
            <option value="DATE_ASC">{t('files.oldestFirst')}</option>
            <option value="NAME_ASC">{t('files.nameAZ')}</option>
            <option value="NAME_DESC">Name (Z-A)</option>
            <option value="SIZE_DESC">{t('files.largestSize')}</option>
            <option value="SIZE_ASC">Smallest size</option>
          </select>

          {/* 5 View Mode Segmented Switcher (Desktop) */}
          <div className="hidden sm:flex items-center p-0.5 rounded-lg bg-muted border border-border">
            <button
              onClick={() => handleViewModeChange('grid')}
              className={`p-1.5 rounded-md transition ${
                viewMode === 'grid' ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'
              }`}
              title="Grid View"
              aria-label="Grid View"
            >
              <Grid className="h-3.5 w-3.5" />
            </button>

            <button
              onClick={() => handleViewModeChange('gallery')}
              className={`p-1.5 rounded-md transition ${
                viewMode === 'gallery' ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'
              }`}
              title="Gallery View (Large Cards)"
              aria-label="Gallery View"
            >
              <LayoutGrid className="h-3.5 w-3.5" />
            </button>

            <button
              onClick={() => handleViewModeChange('list')}
              className={`p-1.5 rounded-md transition ${
                viewMode === 'list' ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'
              }`}
              title="Detailed List View"
              aria-label="Detailed List View"
            >
              <List className="h-3.5 w-3.5" />
            </button>

            <button
              onClick={() => handleViewModeChange('compact')}
              className={`p-1.5 rounded-md transition ${
                viewMode === 'compact' ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'
              }`}
              title="Compact View"
              aria-label="Compact View"
            >
              <LayoutList className="h-3.5 w-3.5" />
            </button>

            <button
              onClick={() => handleViewModeChange('media')}
              className={`p-1.5 rounded-md transition ${
                viewMode === 'media' ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'
              }`}
              title="Media / Visual View"
              aria-label="Media View"
            >
              <Layers className="h-3.5 w-3.5" />
            </button>
          </div>

          {/* Mobile Collapsed View Selector */}
          <div className="sm:hidden relative">
            <select
              value={viewMode}
              onChange={(e) => handleViewModeChange(e.target.value as ViewMode)}
              className="h-8 pl-2 pr-6 bg-card border border-border rounded-lg text-xs font-semibold text-foreground appearance-none"
            >
              <option value="grid">Grid</option>
              <option value="gallery">Gallery</option>
              <option value="list">List</option>
              <option value="compact">Compact</option>
              <option value="media">Media</option>
            </select>
            <ChevronDown className="h-3.5 w-3.5 absolute right-2 top-2.5 text-muted-foreground pointer-events-none" />
          </div>
        </div>
      </div>

      {/* Files Display Content */}
      <div className="flex-1 overflow-y-auto p-6">
        {isLoading ? (
          <div className="h-64 flex items-center justify-center text-muted-foreground text-xs">
            {t('common.loading')}
          </div>
        ) : filteredAndSortedFiles.length === 0 ? (
          <div className="h-full flex flex-col items-center justify-center text-center p-8">
            <div className="w-16 h-16 rounded-2xl bg-primary/10 border border-primary/20 flex items-center justify-center mb-4 text-primary shadow-sm">
              <HardDrive className="h-8 w-8" />
            </div>
            <h3 className="text-base font-bold text-foreground mb-1">
              {searchQuery ? t('files.noMatchingFiles') : t('files.noFiles')}
            </h3>
            <p className="text-xs text-muted-foreground max-w-sm mb-5 leading-relaxed">
              {searchQuery
                ? t('files.searchNoResultsDesc')
                : t('files.noFilesDesc')}
            </p>
            <button
              onClick={() => setIsUploadModalOpen(true)}
              className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground text-xs font-semibold rounded-xl hover:bg-primary/90 transition shadow-sm"
            >
              <Upload className="h-4 w-4" />
              {t('files.uploadFirstFile')}
            </button>
          </div>
        ) : viewMode === 'grid' ? (
          /* VIEW 1: GRID VIEW */
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {filteredAndSortedFiles.map((file) => {
              return (
                <div
                  key={file.id}
                  onClick={() => setPreviewFile(file)}
                  className="group relative rounded-2xl border border-border/80 bg-card hover:bg-muted/20 hover:border-primary/40 transition overflow-hidden cursor-pointer flex flex-col justify-between shadow-sm"
                >
                  {/* Thumbnail Container */}
                  <div className="h-36 bg-muted/40 flex items-center justify-center overflow-hidden border-b border-border/80 relative">
                    {currentWorkspace && (
                      <FileThumbnail
                        workspaceId={currentWorkspace.id}
                        fileId={file.id}
                        fileName={file.fileName}
                        fileType={file.fileType}
                        fileCategory={file.fileCategory}
                        getFileIcon={getFileIcon}
                      />
                    )}

                    {/* Quick Preview Hover Overlay */}
                    <div className="absolute inset-0 bg-black/30 opacity-0 group-hover:opacity-100 transition flex items-center justify-center gap-2">
                      <span className="px-3 py-1.5 rounded-xl bg-card/90 backdrop-blur text-foreground text-[11px] font-semibold flex items-center gap-1.5 shadow">
                        <Eye className="h-3.5 w-3.5" />
                        Preview
                      </span>
                    </div>
                  </div>

                  {/* Body Info */}
                  <div className="p-3.5 flex flex-col gap-1.5">
                    <h4 className="text-xs font-bold text-foreground truncate group-hover:text-primary transition" title={file.fileName}>
                      {file.fileName}
                    </h4>

                    <div className="flex items-center justify-between text-[11px] text-muted-foreground">
                      <span>{formatFileSize(file.fileSize)}</span>
                      <span>{formatDate(file.createdAt)}</span>
                    </div>

                    <div className="flex items-center justify-between text-[11px] text-muted-foreground/80 pt-2 border-t border-border/60 mt-1">
                      <span className="truncate font-medium text-foreground/80">By {file.uploader?.displayName || 'User'}</span>
                      {file.channelName && (
                        <span className="bg-muted px-1.5 py-0.5 rounded text-muted-foreground font-mono text-[10px]">#{file.channelName}</span>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ) : viewMode === 'gallery' ? (
          /* VIEW 2: LARGE GALLERY VIEW */
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6">
            {filteredAndSortedFiles.map((file) => {
              return (
                <div
                  key={file.id}
                  onClick={() => setPreviewFile(file)}
                  className="group relative rounded-2xl border border-border/80 bg-card hover:bg-muted/20 hover:border-primary/40 transition overflow-hidden cursor-pointer flex flex-col justify-between shadow-md"
                >
                  <div className="h-56 bg-muted/40 flex items-center justify-center overflow-hidden border-b border-border/80 relative">
                    {currentWorkspace && (
                      <FileThumbnail
                        workspaceId={currentWorkspace.id}
                        fileId={file.id}
                        fileName={file.fileName}
                        fileType={file.fileType}
                        fileCategory={file.fileCategory}
                        getFileIcon={getFileIcon}
                      />
                    )}

                    <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition flex items-center justify-center">
                      <span className="px-4 py-2 rounded-xl bg-card/90 backdrop-blur text-foreground text-xs font-bold flex items-center gap-2 shadow-lg">
                        <Eye className="h-4 w-4" />
                        Open Preview
                      </span>
                    </div>
                  </div>

                  <div className="p-4 flex flex-col gap-2">
                    <div className="flex items-start justify-between gap-2">
                      <h4 className="text-sm font-bold text-foreground truncate group-hover:text-primary transition">{file.fileName}</h4>
                      <span className="px-2 py-0.5 rounded bg-muted text-[10px] font-mono font-semibold uppercase flex-shrink-0">
                        {file.fileCategory || 'OTHER'}
                      </span>
                    </div>

                    <div className="flex items-center justify-between text-xs text-muted-foreground">
                      <span>By {file.uploader?.displayName || 'User'}</span>
                      <span>{formatDate(file.createdAt)}</span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ) : viewMode === 'list' ? (
          /* VIEW 3: DETAILED LIST VIEW */
          <div className="rounded-2xl border border-border bg-card overflow-hidden shadow-sm">
            <table className="w-full text-left text-xs text-foreground">
              <thead className="bg-muted/50 border-b border-border text-[11px] font-bold text-muted-foreground uppercase tracking-wider">
                <tr>
                  <th className="py-3.5 px-4">Name</th>
                  <th className="py-3.5 px-4">Category</th>
                  <th className="py-3.5 px-4">Size</th>
                  <th className="py-3.5 px-4">Owner</th>
                  <th className="py-3.5 px-4">Location</th>
                  <th className="py-3.5 px-4">Date</th>
                  <th className="py-3.5 px-4 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border/60">
                {filteredAndSortedFiles.map((file) => {
                  const authenticatedDownloadUrl = currentWorkspace
                    ? fileResourceService.getResourceUrl(currentWorkspace.id, file.id, 'download')
                    : '';

                  return (
                    <tr
                      key={file.id}
                      onClick={() => setPreviewFile(file)}
                      className="hover:bg-muted/30 transition cursor-pointer group"
                    >
                      <td className="py-3 px-4 font-semibold text-foreground flex items-center gap-3">
                        {getFileIcon(file.fileType, file.fileCategory)}
                        <span className="truncate max-w-xs group-hover:text-primary transition">{file.fileName}</span>
                      </td>
                      <td className="py-3 px-4 text-muted-foreground">
                        <span className="px-2 py-0.5 rounded-md bg-muted text-[10px] font-mono font-semibold uppercase">
                          {file.fileCategory || 'OTHER'}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-muted-foreground font-mono text-[11px]">{formatFileSize(file.fileSize)}</td>
                      <td className="py-3 px-4 text-foreground/90 font-medium">{file.uploader?.displayName || 'User'}</td>
                      <td className="py-3 px-4 text-muted-foreground">
                        {file.channelName ? `#${file.channelName}` : file.conversationId ? 'Direct Message' : 'Workspace File'}
                      </td>
                      <td className="py-3 px-4 text-muted-foreground text-[11px]">{formatDate(file.createdAt)}</td>
                      <td className="py-3 px-4 text-right" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center justify-end gap-1">
                          <button
                            onClick={() => setPreviewFile(file)}
                            className="p-1.5 rounded-lg hover:bg-muted text-muted-foreground hover:text-primary transition"
                            title="Preview"
                          >
                            <Eye className="h-4 w-4" />
                          </button>
                          <a
                            href={authenticatedDownloadUrl}
                            download={file.fileName}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="p-1.5 rounded-lg hover:bg-muted text-muted-foreground hover:text-primary transition"
                            title="Download"
                          >
                            <Download className="h-4 w-4" />
                          </a>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : viewMode === 'compact' ? (
          /* VIEW 4: COMPACT HIGH-DENSITY VIEW */
          <div className="rounded-2xl border border-border bg-card overflow-hidden shadow-sm divide-y divide-border/60">
            {filteredAndSortedFiles.map((file) => {
              const authenticatedDownloadUrl = currentWorkspace
                ? fileResourceService.getResourceUrl(currentWorkspace.id, file.id, 'download')
                : '';

              return (
                <div
                  key={file.id}
                  onClick={() => setPreviewFile(file)}
                  className="px-4 py-2 flex items-center justify-between hover:bg-muted/30 transition cursor-pointer group text-xs select-none"
                >
                  <div className="flex items-center gap-3 min-w-0 flex-1">
                    {getFileIcon(file.fileType, file.fileCategory)}
                    <span className="font-semibold text-foreground truncate group-hover:text-primary transition max-w-md">
                      {file.fileName}
                    </span>
                    <span className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-muted text-muted-foreground uppercase hidden md:inline-block">
                      {file.fileCategory || 'OTHER'}
                    </span>
                  </div>

                  <div className="flex items-center gap-6 text-muted-foreground text-[11px] flex-shrink-0">
                    <span className="hidden sm:inline-block">{file.uploader?.displayName || 'User'}</span>
                    <span className="font-mono">{formatFileSize(file.fileSize)}</span>
                    <span className="hidden md:inline-block">{formatDate(file.createdAt)}</span>
                    <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                      <button
                        onClick={() => setPreviewFile(file)}
                        className="p-1 rounded hover:bg-muted text-muted-foreground hover:text-primary transition"
                        title="Preview"
                      >
                        <Eye className="h-3.5 w-3.5" />
                      </button>
                      <a
                        href={authenticatedDownloadUrl}
                        download={file.fileName}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="p-1 rounded hover:bg-muted text-muted-foreground hover:text-primary transition"
                        title="Download"
                      >
                        <Download className="h-3.5 w-3.5" />
                      </a>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          /* VIEW 5: MEDIA / VISUAL VIEW */
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5">
            {filteredAndSortedFiles.map((file) => {
              return (
                <div
                  key={file.id}
                  onClick={() => setPreviewFile(file)}
                  className="group relative rounded-2xl border border-border bg-card hover:border-primary/50 transition overflow-hidden cursor-pointer flex flex-col justify-between shadow"
                >
                  <div className="h-44 bg-slate-900 flex items-center justify-center overflow-hidden relative">
                    {currentWorkspace && (
                      <FileThumbnail
                        workspaceId={currentWorkspace.id}
                        fileId={file.id}
                        fileName={file.fileName}
                        fileType={file.fileType}
                        fileCategory={file.fileCategory}
                        getFileIcon={getFileIcon}
                      />
                    )}

                    <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition flex items-center justify-center">
                      <span className="px-3 py-1.5 rounded-xl bg-card/90 text-foreground text-xs font-bold flex items-center gap-1.5 shadow">
                        <Eye className="h-3.5 w-3.5" />
                        View
                      </span>
                    </div>
                  </div>

                  <div className="p-3">
                    <h4 className="text-xs font-bold text-foreground truncate group-hover:text-primary transition">{file.fileName}</h4>
                    <p className="text-[11px] text-muted-foreground mt-0.5">{formatFileSize(file.fileSize)} • {file.fileCategory}</p>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Upload Modal */}
      <FileUploadModal
        isOpen={isUploadModalOpen}
        onClose={() => setIsUploadModalOpen(false)}
        onSuccess={(uploaded) => {
          setFiles((prev) => [uploaded, ...prev]);
        }}
      />

      {/* Preview Modal */}
      <FilePreviewModal
        file={previewFile}
        filesList={filteredAndSortedFiles}
        isOpen={!!previewFile}
        onClose={() => setPreviewFile(null)}
        onSelectFile={(selected) => setPreviewFile(selected)}
        onDeleteSuccess={(deletedId) => {
          setFiles((prev) => prev.filter((f) => f.id !== deletedId));
        }}
      />
    </div>
  );
};
